import { cloneDeep, difference, first, last } from 'lodash'
import semverGt from 'semver/functions/gt'
import semverLte from 'semver/functions/lte'
import migrationsList from './migrations_list'
import migrators from './migrations'
import { toSemver } from './toSemver'
import { withoutProtocol } from '../helpers/file'

/**
 * @typedef {import('../index').PlottrFile} PlottrFile
 * @typedef {import('../index').Logger} Logger
 */

export default class Migrator {
  /**
   * @param {PlottrFile} data
   * @param {String | Null} fileURL
   * @param {String} fileVersion
   * @param {String} appVersion
   * @param {(function(String, String, function(Error): Void): Void) | Null} backupFunction
   * @param {Logger} logger
   */
  constructor(data, fileURL, fileVersion, appVersion, backupFunction, logger) {
    this.fileURL = fileURL
    /**
     * @param {Error} error
     * @param {String} message
     */
    this.logError = (error, message) =>
      logger ? logger.error(error, message) : console.error(error, message)
    this.data = cloneDeep(data)
    this.fileVersion = fileVersion
    this.appVersion = appVersion
    /**
     * @type {Array<String>}
     */
    this.migrations = []
    this.migrationsChecked = false
    this.backupFunction = backupFunction

    if (!this.data.file.initialVersion) {
      this.data.file.initialVersion = this.fileVersion
    }
  }

  /**
   * @param {function(String | Null, PlottrFile): Void} callback
   */
  migrate = (callback) => {
    // save a backup file
    if (this.backupFunction && this.fileURL) {
      this.backupFunction(
        `${(withoutProtocol(this.fileURL) ?? 'Unnamed').replace('.pltr', '')}-${this.fileVersion}-backup.pltr`,
        JSON.stringify(this.data, null, 2),
        (err) => {
          if (err) {
            this.logError(err, 'Error backing up')
            callback('backup', this.data)
          } else {
            this.startMigrations(callback)
          }
        }
      )
    } else {
      this.startMigrations(callback)
    }
  }

  /**
   * @return {PlottrFile}
   */
  migrateSync = () => {
    return this.startMigrationsSync()
  }

  /**
   * @return {PlottrFile}
   */
  startMigrationsSync = () => {
    let migrations = this.getMigrations()
    migrations.forEach((m) => {
      const cleaned = m.replace('*', '')
      /**
       * @type {any}
       */
      // We'd have to enumerate all the migrations to make the type
      // checker happy here.
      //
      // @ts-ignore
      const migrator = migrators[cleaned]
      if (migrator) {
        this.data = migrator(this.data)
        if (!this.data.file.appliedMigrations) {
          this.data.file.appliedMigrations = []
        }
        this.data.file.appliedMigrations.push(m)
      }
    })
    this.data.file.version = this.appVersion
    return this.data
  }

  /**
   * @param {function(String | Null, PlottrFile): Void} callback
   */
  startMigrations = (callback) => {
    const migrated = this.startMigrationsSync()
    callback(null, migrated)
  }

  needsToMigrate = () => {
    if (!this.fileVersion) return false
    return this.getMigrations().length
  }

  plottrBehindFile = () => {
    // file version is greater than app
    if (semverGt(this.fileVersion, this.appVersion)) {
      // check if the file has a breaking migration that is ahead of the current version
      const appliedMigrations = this.data.file.appliedMigrations || []
      const breakingMigrations = appliedMigrations.filter((mig) => mig.includes('*'))
      return breakingMigrations.some((mig) => semverGt(toSemver(mig), this.appVersion))
    } else {
      return false
    }
  }

  getMigrations = () => {
    if (this.migrationsChecked) {
      return this.migrations
    }

    this.migrations = migrationsList.filter((version) => {
      if (!this.fileVersion) return true
      const initialVersion = this.data.file.initialVersion
      const semVerVersion = toSemver(version)
      return (
        semverLte(semVerVersion, this.appVersion) &&
        (!initialVersion || semverGt(semVerVersion, initialVersion))
      )
    })
    const appliedMigrations = this.data.file.appliedMigrations ?? []
    this.migrations = difference(this.migrations, appliedMigrations)
    if (appliedMigrations && appliedMigrations.length && this.migrations.length) {
      const lastMigrationVersion = last(appliedMigrations)
      const firstMigrationVersion = first(this.migrations)

      if (lastMigrationVersion && firstMigrationVersion) {
        const latestAppliedMigration = toSemver(lastMigrationVersion)
        const firstMigrationToApply = toSemver(firstMigrationVersion)
        if (semverGt(latestAppliedMigration, firstMigrationToApply)) {
          throw new Error(
            `Can't migrate from version ${latestAppliedMigration} to version
${firstMigrationToApply}`
          )
        }
      }
    }
    this.migrationsChecked = true
    return this.migrations
  }
}
